import {StorageKeys} from "../@types/general.ts";
import {cfg} from "../config.ts";
import {CtrMembres, CtrPhotos, Maybe} from "../graphql/generated/graphql-main.tsx";
import {TSafePropertyType} from "../@types/models.ts";
import murmurhash3js from "murmurhash3js";

const {api, wp} = cfg;
export const apiUrls = {
    gql: () => {
        if (api.portless)
            return `${cfg.protocol}://${api.host}/${api.graphql_path}`;
        else
            return `${cfg.protocol}://${api.host}:${api.port}/${api.graphql_path}`;
    },
    rest: () => {
        if (api.portless)
            return `${cfg.protocol}://${api.host}`;
        else
            return `${cfg.protocol}://${api.host}:${api.port}`;
    },
    wp: () => {
        return null;
    }
}

/**
 * Group genres related to single-family home & plex to avoid too many sub-genres that are meaningless to user;
 * Patrice did not want to see [Quintuplex,Quadruplex] separately, etc.
 * Note: voluntarily omit the label since it must be translated in a React component
 */
export const propertyTypeGroups: { home: TSafePropertyType, plex: TSafePropertyType } = {
    home: {key: "home", label: "", value: ["ME", "MEM", "MM", "MPM", "PP"]},
    plex: {key: "plex", label: "", value: ["4X", "5X"]}
}

/**
 * Generate url to webp photo as downloaded from Centris in local static file server
 * @param photo
 * @param print
 */
export const webpUrl = (photo: CtrPhotos, print?: boolean) => {
    const rootUrl = `${cfg.protocol}://${cfg.media.root}/${cfg.media.path_listings}`;
    const mls = photo.NO_INSCRIPTION;
    const idx = photo.SEQ?.padStart(2, '0');
    const prefix = `${rootUrl}/${mls}/${idx}`;
    return print ? `${prefix}-print.webp` : `${prefix}.webp`;
}

/**
 * Given any HTML DOM element, check if it's hidden by any method
 * @source MS Copilot
 * @param element
 */
export const isElementHidden = (element?: HTMLElement | null): boolean => {
    if (element == null) return true;
    // Check if the element itself or its ancestors are hidden
    if (element.offsetParent === null)
        return true;

    // Check if display is set to none
    if (window.getComputedStyle(element).display === 'none')
        return true;

    // Check if visibility is set to hidden
    if (window.getComputedStyle(element).visibility === 'hidden')
        return true;

    // Check if opacity is set to 0
    if (window.getComputedStyle(element).opacity === '0')
        return true;

    // The element is visible
    return false;
}

/**
 * Given a broker, try to get its official photo (provided by E&V) instead of the Centris photo;
 * this official photo is stored on our internal media server, so it does not depend on Centris.
 * @param broker
 * @param args
 */
export const officialBrokerPhoto = (broker: Maybe<CtrMembres>, args?: {
    isMcgill?: Maybe<boolean>,
    format?: Maybe<"webp" | "jpg">
}) => {
    // Brave's Leo also recommends this awesome library: https://www.npmjs.com/package/remove-accents
    // replace [.'] and 'whitespace' with empty string (e.g. David O'Malley, Brigitte I. Burdman, etc.)
    const saneName = removeAccents(`${broker?.PRENOM} ${broker?.NOM}`).replaceAll(/[.'\s]/g, "");
    const fileName = `${broker?.CODE}_${saneName}`; // <broker-id>_<sane-name> (extension is added separately)
    const mediaUrl = `${cfg.protocol}://${cfg.media.root}/${cfg.media.path_brokers}`;
    // 1 broker may have different picture for McGill vs EV
    const subPath = args?.isMcgill ? cfg.media.subpath_mcgill : cfg.media.subpath_ev;
    return {
        url: `${mediaUrl}/${subPath}/${fileName}.${args?.format ?? "webp"}`,
        jpg: `${fileName}.jpg`,
        webp: `${fileName}.webp`
    };
}

/**
 * Format a website URL for display while preserving meaningful path/query/hash details.
 * Root paths are only parser defaults, so hide them to avoid labels like `anneturcotte.evquebec.com/`.
 */
export const displayWebsiteUrl = (site: string) => {
    const url = new URL(site);
    const pathname = url.pathname === "/" ? "" : url.pathname;
    return `${url.host}${pathname}${url.search}${url.hash}`;
}

/**
 * Convert string to 128-bit MurmurHash for use in translations and other areas that require bi-directional
 * unique IDs w/o need for cryptographic security of the ID.
 * @param txt
 * @param as64 - whether to force 128-bit MurmurHash v3 to 64-bit string
 */
export function str2hash(txt: string, as64: boolean = false) {
    const hash = murmurhash3js.x64.hash128(txt);
    return as64 ? hash.slice(0, 16) : hash;
}

/**
 * @deprecated No longer used for anything in React frontend; same-name function exists in backend (nodejs)
 *  only due to Matrix scraper relying on older custom IDs generated via V1 str2key.
 * Convert a phrase or string into a unique key; not ideal for translation due to risk of collision
 * since we remove all punctuation. Instead, use MurmurHash 3 (128-bit) to generate a unique key.
 * This is still used in Matrix scraper so as not to reset all the custom IDs currently in the db.
 * @param txt
 */
export function str2keyV1(txt: string) {
    return removeAccents(txt)
        .toLowerCase()
        .replaceAll(/\s+/g, '-') // replace whitespace w/ '-'
        .replaceAll(/[^a-z0-9_-]/g, ''); // remove all remaining non-alphanumeric characters
}

/**
 * Same as "str2keyV1" but also removes multiple dashes (e.g. rue---canton).
 * However, use it on demande, else existing keys relying on V1 will be reset.
 * e.g. the Matrix scraper uses it for agency name, so this would duplicate things badly
 * @param txt
 */
export function str2key(txt: string) {
    return removeAccents(txt)
        .toLowerCase()
        .replaceAll(/\s+/g, '-') // replace whitespace w/ '-'
        .replaceAll(/[^a-z0-9_\-]/g, '-') // remove all remaining non-alphanumeric characters
        .replaceAll(/-+/g, '-'); // sometimes multiple dashes (e.g. rue---canton)
}

/**
 * This solution was provided by Brave's Leo as a way to replace accented French accents with their non-accented
 * equivalent, thus preserving the meaning of the string without the accents.
 * Leo also recommended this awesome library https://www.npmjs.com/package/remove-accents,
 * but I like understanding how it works first before relying on third-party libraries.
 * Note: the normalize("NFD") is really the key here as it separates the accents from the letter, thus
 *  allowing to then do the replace on those invisible accents while leaving the non-accented characters.
 * @param txt
 */
export const removeAccents = (txt: string) => {
    return txt.normalize("NFD").replace(/[\u0300-\u036f]/g, '');
}

/**
 * Conditionally prefix a value by something if the value is non-null and not empty
 * @param prefix
 * @param val
 */
export const prefix = (prefix: string, val: Maybe<string>) => {
    if (val == null || strEmpty(val)) return "";
    return `${prefix} ${val}`;
}

/**
 * Apollo includes `__typename` in all its responses, BUT `__typename` is incompatible w/ GQL input types.
 * @source https://stackoverflow.com/questions/47211778/cleaning-unwanted-fields-from-graphql-responses/51380645#51380645
 * @param state
 */
export const gqlClean = (state: any) => {
    let tmp = JSON.stringify(state, (key, value) =>
        (key === '__typename' ? undefined : value))
    return JSON.parse(tmp)
}

/**
 * From year 'from' 'plus' X years, generate array of years
 * @param from
 * @param count
 */
export const years = (from: number, count: number) => {
    const years = [];
    for (let year = from; year <= from + count; year++) {
        years.push(year);
    }
    return years;
}

/**
 *
 * @param parent
 * @param selector
 * @param value
 */
export const setInputValue = (parent: HTMLElement | null, selector: string, value: string): boolean => {
    try {
        parent!.querySelector<HTMLInputElement>(selector)!.value = value
        return true
    } catch (e) {
        console.error(`ERROR setting input value -> ${selector}`)
        console.error(e)
        return false
    }
}

/**
 * Helper to check if array|object is empty
 * @param object
 */
export const empty = (object: Array<any> | Object | null): boolean => {
    if (object == null) return true; // null = empty by definition
    if (Array.isArray(object))
        return !object.length;
    else
        return !Object.keys(object).length;
}

/**
 *
 * @param value
 */
export const strEmpty = (value?: string | null): boolean => {
    return !value || value?.length == 0;
}

/**
 * Given [HTMLElement.dataset] instance, recursively convert to full JSON object, converting nested values
 * from stringified JSON string to full JSON object using 'JSON.parse'.
 * @param {DOMStringMap} dataset
 */
export const dataset2json = (dataset: DOMStringMap): any => {
    const json: any = {}
    for (const x in dataset) {
        try {
            json[x] = JSON.parse(dataset[x]!)
        } catch (e) {
            json[x] = dataset[x]!
        }
    }
    return json
}

/**
 * Use JS native Intl functions to customize display of date
 * - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/formatToParts
 * @param date - the [Date] object to be printed nicely
 * @param opts - user-defined custom options
 */
export const niceDate = (date: Date, opts?: Intl.DateTimeFormatOptions) => {
    const options: Intl.DateTimeFormatOptions = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "America/Montreal",
        ...opts
    }
    const format = new Intl.DateTimeFormat('fr-CA', options)
    // console.info(format.formatToParts(date))
    return format.format(date)
}

/**
 * Reset TIME portion of a date in UTC to avoid issues w/ current 'time-of-day' causing wrong calculations
 *
 * @param date
 */
export const resetTimeDate = (date: Date) => {
    date.setHours(0)
    date.setMinutes(0)
    date.setSeconds(0)
    date.setMilliseconds(0)
    return date
}

export const resetTimeDateUtc = (date: Date) => {
    date.setUTCHours(0)
    date.setUTCMinutes(0)
    date.setUTCSeconds(0)
    date.setUTCMilliseconds(0)
    return date
}

/**
 * All Apollo Client query responses include the `__typename` field, but when returning data
 * to a mutation to update a state in the database, we CANNOT include the `__typename` because the
 * Graph schema is NOT defined to include it, so we have to strip it
 *
 * @sources
 *  - https://stackoverflow.com/questions/47211778/cleaning-unwanted-fields-from-graphql-responses/51380645#51380645
 * @param state
 */
export const stripTypename = (state: any) => {
    let tmp = JSON.stringify(
        state,
        (key, value) => (key === '__typename' ? undefined : value)
    )
    return JSON.parse(tmp)
}

/**
 * Copilot-generated function to strip `__typename` from Apollo Client query responses
 * by doing a proper iteration of objects instead of using JSON.stringify.
 * @param state
 */
export const stripTypenameImproved = (state: any): any => {
    if (Array.isArray(state)) {
        return state.map(stripTypenameImproved);
    } else if (state !== null && typeof state === 'object') {
        const newObj: any = {};
        for (const key in state) {
            if (key !== '__typename') {
                newObj[key] = stripTypenameImproved(state[key]);
            }
        }
        return newObj;
    }
    return state;
}

/**
 * Given a string, extract first X words (count provided in parameters)
 * @param str
 * @param num
 */
export const firstWords = (str: string, num: number) => {
    let nWords: string[] = str.split(" ")
    let suffix = nWords.length > num ? "..." : ""

    const final = nWords.slice(0, num).join(" ")
    return `${final}${suffix}`
}

/**
 * Given raw phone number, format it to proper North American format
 * https://www.basedash.com/blog/format-phone-numbers-in-javascript
 * @param phoneNumberString
 */
export const formatPhoneNumber = (phoneNumberString?: string | null) => {
    if (phoneNumberString == null) return null;
    let cleaned = ('' + phoneNumberString.trim()).replace(/\D/g, '');
    let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

    if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return null;
}

/**
 * https://bobbyhadz.com/blog/javascript-check-if-two-arrays-have-same-elements
 * @param arr1
 * @param arr2
 */
export const areEqual = (arr1: string[], arr2: string[]): boolean => {
    if (arr1.length !== arr2.length)
        return false
    return arr1.every((elem) => arr2.includes(elem))
}


/**
 * Helpful tool to convert hex color code to rgb w/ custom opacity
 *
 * @sources
 *  - https://gist.github.com/danieliser/b4b24c9f772066bcf0a6
 *
 * @param hexCode
 * @param opacity - 0-100
 */
export const hex2rgba = (hexCode: string, opacity: number) => {
    let hex = hexCode.replace('#', '');

    if (hex.length === 3) {
        hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r},${g},${b},${opacity / 100})`;
}

/**
 * Great helper function to allow type-checking of object keys in places
 * where using direct type is NOT allowed (e.g. Mongo `sort`)
 */
export const nameof = <T>(key: keyof T): keyof T => key

/**
 * Browser Storage - localStorage & sessionStorage
 */
const storageSet = <T>(key: StorageKeys | string, storage: Storage, value: T) => {
    let tmp: any = value;
    if (typeof value == "object") {
        tmp = JSON.stringify(value);
    }
    storage.setItem(key, tmp);
}
const storageGet = <T>(key: StorageKeys | string, storage: Storage, isJson: boolean = false) => {
    const item: string | null = storage.getItem(key);
    try {
        if (isJson && item != null) {
            return JSON.parse(item) as T;
        }
        return item as T;
    } catch (e) {
        console.error(e);
        return null;
    }
}

export const localSet = <T>(key: string, value: T) =>
    storageSet<T>(key, window.localStorage, value);
export const localGet = <T>(key: string) =>
    storageGet<T>(key, window.localStorage, false);
export const localGetJson = <T>(key: string) =>
    storageGet<T>(key, window.localStorage, true);

export const sessionSet = <T>(key: string, value: T) =>
    storageSet<T>(key, window.sessionStorage, value);
export const sessionGet = <T>(key: string) =>
    storageGet<T>(key, window.sessionStorage, false);
export const sessionGetJson = <T>(key: string) =>
    storageGet<T>(key, window.sessionStorage, true);
